import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Typography, Card, Chip, Button, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Avatar, Tooltip,
    IconButton, CircularProgress, Alert, Snackbar,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
    Tabs, Tab, Badge, Collapse, InputAdornment, TablePagination,
    Select, MenuItem, FormControl, InputLabel, Menu,
    useTheme, useMediaQuery,
} from '@mui/material';
import {
    CheckCircleOutline as ApproveIcon,
    CancelOutlined as RejectIcon,
    CalendarToday as CalendarIcon,
    Edit as EditIcon,
    HourglassEmpty as EmptyIcon,
    Refresh as RefreshIcon,
    PendingActions as PendingIcon,
    FormatListBulleted as AllIcon,
    ExpandMore as ExpandIcon,
    ExpandLess as CollapseIcon,
    Search as SearchIcon,
    Business as DeptIcon,
    Domain as CompanyIcon,
    Group as TeamIcon,
    AttachFile as AttachIcon,
    OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { getData, postData, putData } from '../../services/axiosInstance';
import { API_ENDPOINTS } from '../../services/endpoints';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(str) {
    if (!str) return '';
    return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function fmtDay(str) {
    if (!str) return '';
    return DAY_ABBR[new Date(str + 'T00:00:00').getDay()];
}

const L2_STATUS_CFG = {
    PENDING: { label: 'Pending', bg: '#fff7ed', color: '#c2410c', border: '#fdba74' },
    APPROVED: { label: 'Approved', bg: '#ecfdf5', color: '#059669', border: '#6ee7b7' },
    REJECTED: { label: 'Rejected', bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' },
    NOT_REQUIRED: { label: 'Not Required', bg: '#f1f5f9', color: '#64748b', border: '#cbd5e1' },
};

function L2StatusChip({ status }) {
    const cfg = L2_STATUS_CFG[status] || L2_STATUS_CFG.NOT_REQUIRED;
    return (
        <Chip label={cfg.label} size="small"
            sx={{
                bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
                fontWeight: 700, fontSize: 10, height: 22, maxWidth: 'none',
                '& .MuiChip-label': { overflow: 'visible', textOverflow: 'unset', whiteSpace: 'nowrap', px: 1 },
            }} />
    );
}

const MONTHS = [
    { value: 0, label: 'All Months' },
    { value: 1, label: 'January' }, { value: 2, label: 'February' },
    { value: 3, label: 'March' }, { value: 4, label: 'April' },
    { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' },
    { value: 9, label: 'September' }, { value: 10, label: 'October' },
    { value: 11, label: 'November' }, { value: 12, label: 'December' },
];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = ['All', CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];

const selectSx = {
    fontSize: 13, borderRadius: 1.5, height: 36, bgcolor: '#fff',
    '& .MuiSelect-select': { py: 0.75, fontSize: 13 },
};

// ─── Entry Table ──────────────────────────────────────────────────────────────

const DAY_NAMES_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_COLORS_L2 = {
    Mon: '#6366f1', Tue: '#8b5cf6', Wed: '#06b6d4',
    Thu: '#10b981', Fri: '#f59e0b', Sat: '#ef4444', Sun: '#ef4444',
};

function EntryTable({ group, acting, editingId, editVals, onApprove, onReject, onStartEdit, onCancelEdit, onEditChange, onSaveEdit, editSaving, editError, showActions }) {
    // Group entries by date for visual grouping
    const dateGroups = React.useMemo(() => {
        const map = {};
        group.entries.forEach(e => {
            const d = e.entry_date || 'no-date';
            if (!map[d]) map[d] = [];
            map[d].push(e);
        });
        return map;
    }, [group.entries]);

    const hasAnyMultiEntryDate = Object.values(dateGroups).some(arr => arr.length > 1);

    return (
        <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 700, tableLayout: 'fixed' }}>
                <TableHead>
                    <TableRow sx={{ bgcolor: '#f8faff', borderBottom: '2px solid #e8eeff' }}>
                        {[
                            { label: 'Date', width: '9%' },
                            { label: 'Project / Task', width: '18%' },
                            { label: 'Description', width: 'auto' },
                            { label: 'Hours', width: '10%' },
                            { label: 'Type', width: '9%' },
                            { label: 'Files', width: '7%' },
                            { label: showActions ? 'Actions' : 'Status', width: '12%' },
                        ].map(({ label, width }) => (
                            <TableCell key={label} sx={{
                                fontWeight: 700, fontSize: 11, color: '#1D4ED8', py: 1.25,
                                textTransform: 'uppercase', letterSpacing: '0.04em',
                                borderBottom: '2px solid #e8eeff', width,
                                ...(label === 'Actions' || label === 'Status' ? { textAlign: 'center' } : {}),
                            }}>
                                {label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {group.entries.map((entry) => {
                        const isActing = !!acting[entry.id];
                        const isEditing = editingId === entry.id;
                        const ev = editVals[entry.id] || {};
                        const currentBillable = ev.is_billable !== undefined ? ev.is_billable : entry.is_billable;

                        // Check if this entry starts a new date group with multiple entries
                        const entriesOnSameDay = dateGroups[entry.entry_date] || [];
                        const isFirstOfMultiDay = hasAnyMultiEntryDate
                            && entriesOnSameDay.length > 1
                            && entriesOnSameDay[0].id === entry.id;

                        const dayName = entry.entry_date ? DAY_NAMES_SHORT[new Date(entry.entry_date + 'T00:00:00').getDay()] : '';
                        const dayColor = DAY_COLORS_L2[dayName] || '#64748b';
                        const dayTotal = entriesOnSameDay.reduce((s, e) => s + Number(e.hours_worked || 0), 0);
                        const isApprovedUntouched = entry.l2_status === 'APPROVED' && !entry.l2_reopened_from_approved;
                        const isReopened = !!entry.l2_reopened_from_approved;
                        const statusBg = isApprovedUntouched ? '#f0fdf4' : isReopened ? '#eff6ff' : undefined;
                        const statusHoverBg = isApprovedUntouched ? '#dcfce7' : isReopened ? '#dbeafe' : '#fafbff';

                        return (
                            <React.Fragment key={entry.id}>
                                {/* Same-day group header — only when 2+ entries share the same date */}
                                {isFirstOfMultiDay && (
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ py: 0.75, px: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dayColor }} />
                                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: dayColor }}>
                                                    {dayName} · {fmtDate(entry.entry_date)}
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                                                    — {entriesOnSameDay.length} entries · {dayTotal}h total
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )}
                                <TableRow sx={{ '&:hover': { bgcolor: statusHoverBg }, ...(isEditing ? { bgcolor: '#f8fafc' } : (statusBg ? { bgcolor: statusBg } : {})), ...(isFirstOfMultiDay || (hasAnyMultiEntryDate && entriesOnSameDay.length > 1) ? { '& td:first-of-type': { pl: 3 } } : {}) }}>

                                    {/* DATE */}
                                    <TableCell sx={{ py: 2, borderBottom: '1px solid #f1f5f9' }}>
                                        {entriesOnSameDay.length <= 1 && (
                                            <>
                                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{fmtDay(entry.entry_date)}</Typography>
                                                <Typography sx={{ fontSize: 10.5, color: '#94a3b8', mt: 0.25 }}>{fmtDate(entry.entry_date)}</Typography>
                                            </>
                                        )}
                                    </TableCell>

                                    {/* PROJECT / TASK */}
                                    <TableCell sx={{ py: 2, borderBottom: '1px solid #f1f5f9' }}>
                                        {entry.project_id ? (
                                            <>
                                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a', mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.project_name || '—'}</Typography>
                                                <Box sx={{ display: 'inline-flex', px: 0.75, py: 0.3, bgcolor: '#f1f5f9', borderRadius: 1, border: '1px solid #e2e8f0', maxWidth: '100%', overflow: 'hidden' }}>
                                                    <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.task_name || '—'}</Typography>
                                                </Box>
                                            </>
                                        ) : (
                                            <>
                                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a', mb: entry.day_type === 'Leave' ? 0.5 : 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.day_type || '—'}</Typography>
                                                {entry.day_type === 'Leave' && (
                                                    <Box sx={{ display: 'inline-flex', px: 0.75, py: 0.3, bgcolor: '#f1f5f9', borderRadius: 1, border: '1px solid #e2e8f0', maxWidth: '100%', overflow: 'hidden' }}>
                                                        <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {entry.leave_type || '—'}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </>
                                        )}
                                    </TableCell>

                                    {/* DESCRIPTION */}
                                    <TableCell sx={{ py: 2, borderBottom: '1px solid #f1f5f9' }}>
                                        <Typography sx={{ fontSize: 12, color: '#334155', lineHeight: 1.6, wordBreak: 'break-word' }}>{entry.work_description || '—'}</Typography>
                                        {entry.remarks && (
                                            <Box sx={{ mt: 1.25, p: 1, bgcolor: '#fff7ed', borderRadius: 1.5, borderLeft: '3px solid #fb923c' }}>
                                                <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: '#c2410c', textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.4 }}>Remark</Typography>
                                                <Typography sx={{ fontSize: 11, color: '#92400e', fontStyle: 'italic', lineHeight: 1.55, wordBreak: 'break-word' }}>{entry.remarks}</Typography>
                                            </Box>
                                        )}
                                    </TableCell>

                                    {/* HOURS */}
                                    <TableCell sx={{ py: 2, borderBottom: '1px solid #f1f5f9' }}>
                                        {Number(entry.hours_worked) === 0 ? (
                                            <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>—</Typography>
                                        ) : (
                                            <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', px: 1, py: 0.4, bgcolor: '#f0f4ff', borderRadius: 1.5 }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#1D4ED8', lineHeight: 1.2 }}>{entry.hours_worked}h</Typography>
                                                {entry.overtime_hours > 0 && (
                                                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#f59e0b' }}>+{entry.overtime_hours}OT</Typography>
                                                )}
                                            </Box>
                                        )}
                                    </TableCell>

                                    {/* TYPE */}
                                    <TableCell sx={{ py: 2, borderBottom: '1px solid #f1f5f9' }}>
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.3, borderRadius: 10, bgcolor: currentBillable ? '#ecfdf5' : '#f5f3ff', border: `1px solid ${currentBillable ? '#6ee7b7' : '#ddd6fe'}` }}>
                                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', flexShrink: 0, bgcolor: currentBillable ? '#059669' : '#7c3aed' }} />
                                            <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: currentBillable ? '#059669' : '#7c3aed', whiteSpace: 'nowrap' }}>
                                                {currentBillable ? 'Billable' : 'Non-Bill.'}
                                            </Typography>
                                        </Box>
                                    </TableCell>

                                    {/* FILES */}
                                    <TableCell sx={{ py: 2, borderBottom: '1px solid #f1f5f9' }}>
                                        {Array.isArray(entry.attachment_urls) && entry.attachment_urls.length > 0 ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                {entry.attachment_urls.map((att, i) => {
                                                    const url = typeof att === 'string' ? att : att?.url;
                                                    const name = typeof att === 'string'
                                                        ? decodeURIComponent(att.split('/').pop() || `file-${i + 1}`)
                                                        : (att?.name || `file-${i + 1}`);
                                                    if (!url) return null;
                                                    return (
                                                        <Tooltip key={i} title={name}>
                                                            <Box
                                                                component="a" href={url} target="_blank" rel="noopener noreferrer"
                                                                onClick={e => e.stopPropagation()}
                                                                sx={{
                                                                    display: 'inline-flex', alignItems: 'center', gap: 0.3,
                                                                    px: 0.6, py: 0.3, borderRadius: 1,
                                                                    bgcolor: '#eef2ff', border: '1px solid #c7d2fe',
                                                                    textDecoration: 'none', cursor: 'pointer',
                                                                    '&:hover': { bgcolor: '#e0e7ff' },
                                                                }}
                                                            >
                                                                <AttachIcon sx={{ fontSize: 11, color: '#6366f1', flexShrink: 0 }} />
                                                                <OpenInNewIcon sx={{ fontSize: 9, color: '#818cf8', flexShrink: 0 }} />
                                                            </Box>
                                                        </Tooltip>
                                                    );
                                                })}
                                            </Box>
                                        ) : (
                                            <Typography sx={{ fontSize: 11, color: '#cbd5e1' }}>—</Typography>
                                        )}
                                    </TableCell>

                                    {/* ACTIONS / STATUS */}
                                    <TableCell sx={{ py: 2, borderBottom: '1px solid #f1f5f9', textAlign: 'center' }}>
                                        {showActions && entry.l2_status === 'PENDING' ? (
                                            isActing ? (
                                                <CircularProgress size={18} sx={{ color: '#6366f1' }} />
                                            ) : (
                                                <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'center', alignItems: 'center' }}>
                                                    {entry.external_can_edit && !isEditing && (
                                                        <Tooltip title="Edit entry" arrow>
                                                            <IconButton size="small" onClick={() => onStartEdit(entry)}
                                                                sx={{ color: '#6366f1', '&:hover': { bgcolor: '#eef2ff' }, p: 0.5 }}>
                                                                <EditIcon sx={{ fontSize: 20 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip title={isEditing ? 'Save & Approve' : 'Approve'} arrow>
                                                        <IconButton size="small" onClick={() => onApprove(entry)}
                                                            sx={{ color: '#059669', '&:hover': { bgcolor: '#ecfdf5' }, p: 0.5 }}>
                                                            <ApproveIcon sx={{ fontSize: 20 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Reject" arrow>
                                                        <IconButton size="small" onClick={() => onReject(entry.id)}
                                                            sx={{ color: '#dc2626', '&:hover': { bgcolor: '#fef2f2' }, p: 0.5 }}>
                                                            <RejectIcon sx={{ fontSize: 20 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            )
                                        ) : (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                                <L2StatusChip status={entry.l2_status} />
                                            </Box>
                                        )}
                                    </TableCell>
                                </TableRow>

                                {/* INLINE EDIT ROW */}
                                {isEditing && (
                                    <TableRow key={`edit-${entry.id}`}>
                                        <TableCell colSpan={7} sx={{ bgcolor: '#f0f9ff', borderBottom: '2px solid #bae6fd', px: 2.5, py: 1.5 }}>
                                            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                                <TextField label="Hours" type="number" size="small"
                                                    value={ev.hours_worked ?? ''}
                                                    onChange={e => onEditChange(entry.id, 'hours_worked', e.target.value)}
                                                    inputProps={{ min: 0.5, max: 24, step: 0.5 }}
                                                    sx={{ width: 95 }} />
                                                <Select
                                                    size="small"
                                                    value={ev.is_billable ? 'billable' : 'non-billable'}
                                                    onChange={e => onEditChange(entry.id, 'is_billable', e.target.value === 'billable')}
                                                    sx={{
                                                        fontSize: 12, borderRadius: 1.5, height: 40,
                                                        bgcolor: ev.is_billable ? '#ecfdf5' : '#f5f3ff',
                                                        color: ev.is_billable ? '#059669' : '#7c3aed',
                                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: ev.is_billable ? '#6ee7b7' : '#ddd6fe' },
                                                        '& .MuiSvgIcon-root': { color: ev.is_billable ? '#059669' : '#7c3aed' },
                                                        '& .MuiSelect-select': { py: 0.75, px: 1.5 },
                                                        minWidth: 130,
                                                    }}
                                                >
                                                    <MenuItem value="billable" sx={{ fontSize: 12 }}>Billable</MenuItem>
                                                    <MenuItem value="non-billable" sx={{ fontSize: 12 }}>Non-Billable</MenuItem>
                                                </Select>
                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.25 }}>
                                                    <Button size="small" variant="contained"
                                                        onClick={() => onSaveEdit(entry.id)}
                                                        disabled={editSaving}
                                                        startIcon={editSaving ? <CircularProgress size={12} color="inherit" /> : null}
                                                        sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#6366f1', boxShadow: 'none', fontSize: 12 }}>
                                                        {editSaving ? 'Saving…' : 'Save'}
                                                    </Button>
                                                    <Button size="small" onClick={onCancelEdit} disabled={editSaving}
                                                        sx={{ textTransform: 'none', fontSize: 12, color: '#64748b' }}>Cancel</Button>
                                                </Box>
                                            </Box>
                                            {editError && (
                                                <Typography sx={{ mt: 1, fontSize: 11, color: '#dc2626' }}>{editError}</Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </React.Fragment>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

// ─── Files column button — icon + count, opens a menu to pick which file to view ──

function FilesButton({ attachments }) {
    const [anchorEl, setAnchorEl] = useState(null);

    const files = (Array.isArray(attachments) ? attachments : []).map((att, i) => {
        const url = typeof att === 'string' ? att : att?.url;
        const name = typeof att === 'string'
            ? decodeURIComponent(att.split('/').pop() || `file-${i + 1}`)
            : (att?.name || `file-${i + 1}`);
        return { url, name };
    }).filter(f => f.url);

    if (files.length === 0) {
        return <Typography sx={{ fontSize: 11, color: '#cbd5e1' }}>—</Typography>;
    }

    return (
        <>
            <Tooltip title={`${files.length} file${files.length > 1 ? 's' : ''}`}>
                <Box
                    component="button"
                    type="button"
                    onClick={e => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}
                    sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                        px: 1, py: 0.4, borderRadius: 1.5, border: '1px solid #c7d2fe',
                        bgcolor: '#eef2ff', color: '#4338ca', cursor: 'pointer',
                        '&:hover': { bgcolor: '#e0e7ff' },
                    }}
                >
                    <AttachIcon sx={{ fontSize: 14 }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#4338ca' }}>{files.length}</Typography>
                </Box>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={() => setAnchorEl(null)}
                onClick={e => e.stopPropagation()}
            >
                {files.map((f, i) => (
                    <MenuItem
                        key={i}
                        component="a"
                        href={f.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setAnchorEl(null)}
                        sx={{ fontSize: 12, gap: 1, maxWidth: 280 }}
                    >
                        <AttachIcon sx={{ fontSize: 14, color: '#6366f1' }} />
                        <Typography sx={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                            {f.name}
                        </Typography>
                        <OpenInNewIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

// ─── Entry Mobile Cards ──────────────────────────────────────────────────────

function EntryMobileCards({ group, acting, editingId, editVals, onApprove, onReject, onStartEdit, onCancelEdit, onEditChange, onSaveEdit, editSaving, editError, showActions }) {
    return (
        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {group.entries.map((entry) => {
                const isActing = !!acting[entry.id];
                const isEditing = editingId === entry.id;
                const ev = editVals[entry.id] || {};
                const currentBillable = ev.is_billable !== undefined ? ev.is_billable : entry.is_billable;
                const dayName = entry.entry_date ? DAY_NAMES_SHORT[new Date(entry.entry_date + 'T00:00:00').getDay()] : '';
                const dayColor = DAY_COLORS_L2[dayName] || '#64748b';
                const isApprovedUntouched = entry.l2_status === 'APPROVED' && !entry.l2_reopened_from_approved;
                const isReopened = !!entry.l2_reopened_from_approved;
                const cardBg = isApprovedUntouched ? '#f0fdf4' : isReopened ? '#eff6ff' : '#fff';
                const borderColor = isApprovedUntouched ? '#bbf7d0' : isReopened ? '#bfdbfe' : '#e2e8f0';

                const files = (Array.isArray(entry.attachment_urls) ? entry.attachment_urls : []).map((att, i) => {
                    const url = typeof att === 'string' ? att : att?.url;
                    const name = typeof att === 'string' ? decodeURIComponent(att.split('/').pop() || `file-${i + 1}`) : (att?.name || `file-${i + 1}`);
                    return { url, name };
                }).filter(f => f.url);

                return (
                    <Card key={entry.id} elevation={0} sx={{ border: `1px solid ${borderColor}`, borderRadius: 2, bgcolor: cardBg, overflow: 'hidden' }}>
                        {/* Card header: date + status/actions */}
                        <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${borderColor}` }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dayColor, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: dayColor }}>{dayName}</Typography>
                                <Typography sx={{ fontSize: 11, color: '#64748b' }}>{fmtDate(entry.entry_date)}</Typography>
                            </Box>
                            {/* Actions / Status */}
                            {showActions && entry.l2_status === 'PENDING' ? (
                                isActing ? (
                                    <CircularProgress size={18} sx={{ color: '#6366f1' }} />
                                ) : (
                                    <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center' }}>
                                        {entry.external_can_edit && !isEditing && (
                                            <Tooltip title="Edit entry" arrow>
                                                <IconButton size="small" onClick={() => onStartEdit(entry)} sx={{ color: '#6366f1', '&:hover': { bgcolor: '#eef2ff' }, p: 0.5 }}>
                                                    <EditIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title={isEditing ? 'Save & Approve' : 'Approve'} arrow>
                                            <IconButton size="small" onClick={() => onApprove(entry)} sx={{ color: '#059669', '&:hover': { bgcolor: '#ecfdf5' }, p: 0.5 }}>
                                                <ApproveIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Reject" arrow>
                                            <IconButton size="small" onClick={() => onReject(entry.id)} sx={{ color: '#dc2626', '&:hover': { bgcolor: '#fef2f2' }, p: 0.5 }}>
                                                <RejectIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                )
                            ) : (
                                <L2StatusChip status={entry.l2_status} />
                            )}
                        </Box>

                        {/* Card body */}
                        <Box sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {/* Project / Task or Day Type */}
                            {entry.project_id ? (
                                <Box>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{entry.project_name || '—'}</Typography>
                                    <Box sx={{ display: 'inline-flex', px: 0.75, py: 0.2, bgcolor: '#f1f5f9', borderRadius: 1, border: '1px solid #e2e8f0', mt: 0.3 }}>
                                        <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: '#475569' }}>{entry.task_name || '—'}</Typography>
                                    </Box>
                                </Box>
                            ) : (
                                <Box>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{entry.day_type || '—'}</Typography>
                                    {entry.day_type === 'Leave' && (
                                        <Box sx={{ display: 'inline-flex', px: 0.75, py: 0.2, bgcolor: '#f1f5f9', borderRadius: 1, border: '1px solid #e2e8f0', mt: 0.3 }}>
                                            <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: '#475569' }}>{entry.leave_type || '—'}</Typography>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {/* Description */}
                            {entry.work_description && (
                                <Typography sx={{ fontSize: 11.5, color: '#334155', lineHeight: 1.6 }}>{entry.work_description}</Typography>
                            )}

                            {/* Remark */}
                            {entry.remarks && (
                                <Box sx={{ p: 1, bgcolor: '#fff7ed', borderRadius: 1.5, borderLeft: '3px solid #fb923c' }}>
                                    <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: '#c2410c', textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.3 }}>Remark</Typography>
                                    <Typography sx={{ fontSize: 11, color: '#92400e', fontStyle: 'italic', lineHeight: 1.5 }}>{entry.remarks}</Typography>
                                </Box>
                            )}

                            {/* Hours + Type row */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
                                {Number(entry.hours_worked) === 0 ? (
                                    <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>—</Typography>
                                ) : (
                                    <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', px: 1, py: 0.4, bgcolor: '#f0f4ff', borderRadius: 1.5 }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#1D4ED8', lineHeight: 1.2 }}>{entry.hours_worked}h</Typography>
                                        {entry.overtime_hours > 0 && (
                                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#f59e0b' }}>+{entry.overtime_hours}OT</Typography>
                                        )}
                                    </Box>
                                )}
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.3, borderRadius: 10, bgcolor: currentBillable ? '#ecfdf5' : '#f5f3ff', border: `1px solid ${currentBillable ? '#6ee7b7' : '#ddd6fe'}` }}>
                                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', flexShrink: 0, bgcolor: currentBillable ? '#059669' : '#7c3aed' }} />
                                    <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: currentBillable ? '#059669' : '#7c3aed' }}>
                                        {currentBillable ? 'Billable' : 'Non-Bill.'}
                                    </Typography>
                                </Box>
                                {/* Files */}
                                {files.length > 0 && (
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {files.map((f, i) => (
                                            <Tooltip key={i} title={f.name}>
                                                <Box
                                                    component="a" href={f.url} target="_blank" rel="noopener noreferrer"
                                                    onClick={e => e.stopPropagation()}
                                                    sx={{
                                                        display: 'inline-flex', alignItems: 'center', gap: 0.3,
                                                        px: 0.6, py: 0.3, borderRadius: 1,
                                                        bgcolor: '#eef2ff', border: '1px solid #c7d2fe',
                                                        textDecoration: 'none', cursor: 'pointer',
                                                        '&:hover': { bgcolor: '#e0e7ff' },
                                                    }}
                                                >
                                                    <AttachIcon sx={{ fontSize: 11, color: '#6366f1', flexShrink: 0 }} />
                                                    <OpenInNewIcon sx={{ fontSize: 9, color: '#818cf8', flexShrink: 0 }} />
                                                </Box>
                                            </Tooltip>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* Inline edit panel */}
                        {isEditing && (
                            <Box sx={{ px: 1.5, py: 1.25, bgcolor: '#f0f9ff', borderTop: '2px solid #bae6fd', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                    <TextField label="Hours" type="number" size="small"
                                        value={ev.hours_worked ?? ''}
                                        onChange={e => onEditChange(entry.id, 'hours_worked', e.target.value)}
                                        inputProps={{ min: 0.5, max: 24, step: 0.5 }}
                                        sx={{ width: 95 }} />
                                    <Select
                                        size="small"
                                        value={ev.is_billable ? 'billable' : 'non-billable'}
                                        onChange={e => onEditChange(entry.id, 'is_billable', e.target.value === 'billable')}
                                        sx={{
                                            fontSize: 12, borderRadius: 1.5, height: 40,
                                            bgcolor: ev.is_billable ? '#ecfdf5' : '#f5f3ff',
                                            color: ev.is_billable ? '#059669' : '#7c3aed',
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: ev.is_billable ? '#6ee7b7' : '#ddd6fe' },
                                            '& .MuiSvgIcon-root': { color: ev.is_billable ? '#059669' : '#7c3aed' },
                                            '& .MuiSelect-select': { py: 0.75, px: 1.5 },
                                            minWidth: 130,
                                        }}
                                    >
                                        <MenuItem value="billable" sx={{ fontSize: 12 }}>Billable</MenuItem>
                                        <MenuItem value="non-billable" sx={{ fontSize: 12 }}>Non-Billable</MenuItem>
                                    </Select>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Button size="small" variant="contained"
                                        onClick={() => onSaveEdit(entry.id)}
                                        disabled={editSaving}
                                        startIcon={editSaving ? <CircularProgress size={12} color="inherit" /> : null}
                                        sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#6366f1', boxShadow: 'none', fontSize: 12 }}>
                                        {editSaving ? 'Saving…' : 'Save'}
                                    </Button>
                                    <Button size="small" onClick={onCancelEdit} disabled={editSaving}
                                        sx={{ textTransform: 'none', fontSize: 12, color: '#64748b' }}>Cancel</Button>
                                </Box>
                                {editError && (
                                    <Typography sx={{ fontSize: 11, color: '#dc2626' }}>{editError}</Typography>
                                )}
                            </Box>
                        )}
                    </Card>
                );
            })}
        </Box>
    );
}

// ─── Company Timesheet Entries (responsive) ───────────────────────────────────

function CompanyEntryMobileCards({ workingEntries, entryActing, editingId, editVals, onStartEdit, onCancelEdit, onEditChange, onSaveEdit, onApproveEntry, onRejectEntry, editSaving, editError }) {
    return (
        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {workingEntries.map((entry) => {
                const isActing = !!entryActing?.[entry.id];
                const isEditing = editingId === entry.id;
                const ev = editVals[entry.id] || {};
                const dayName = entry.entry_date ? DAY_NAMES_SHORT[new Date(entry.entry_date + 'T00:00:00').getDay()] : '';
                const dayColor = DAY_COLORS_L2[dayName] || '#64748b';
                const isApprovedUntouched = entry.l2_status === 'APPROVED' && !entry.l2_reopened_from_approved;
                const isReopened = !!entry.l2_reopened_from_approved;
                const cardBg = isApprovedUntouched ? '#f0fdf4' : isReopened ? '#eff6ff' : '#fff';
                const borderColor = isApprovedUntouched ? '#bbf7d0' : isReopened ? '#bfdbfe' : '#e9d5ff';

                const isPending = !entry.l2_status || entry.l2_status === 'PENDING';

                const files = (Array.isArray(entry.attachment_urls) ? entry.attachment_urls : []).map((att, i) => {
                    const url = typeof att === 'string' ? att : att?.url;
                    const name = typeof att === 'string' ? decodeURIComponent(att.split('/').pop() || `file-${i + 1}`) : (att?.name || `file-${i + 1}`);
                    return { url, name };
                }).filter(f => f.url);

                return (
                    <Card key={entry.id} elevation={0} sx={{ border: `1px solid ${borderColor}`, borderRadius: 2, bgcolor: cardBg, overflow: 'hidden' }}>
                        {/* Header: date + status/actions */}
                        <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${borderColor}` }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dayColor, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: dayColor }}>{dayName}</Typography>
                                <Typography sx={{ fontSize: 11, color: '#64748b' }}>{fmtDate(entry.entry_date)}</Typography>
                            </Box>
                            {isPending ? (
                                isActing ? (
                                    <CircularProgress size={18} sx={{ color: '#7c3aed' }} />
                                ) : (
                                    <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center' }}>
                                        {entry.external_can_edit && !isEditing && (
                                            <Tooltip title="Edit entry">
                                                <IconButton size="small" onClick={() => onStartEdit(entry)} sx={{ color: '#7c3aed', p: 0.5 }}>
                                                    <EditIcon sx={{ fontSize: 15 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title={isEditing ? 'Save & Approve' : 'Approve entry'} arrow>
                                            <IconButton size="small" onClick={() => onApproveEntry(entry)} sx={{ color: '#059669', '&:hover': { bgcolor: '#ecfdf5' }, p: 0.5 }}>
                                                <ApproveIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Reject entry" arrow>
                                            <IconButton size="small" onClick={() => onRejectEntry(entry.id)} sx={{ color: '#dc2626', '&:hover': { bgcolor: '#fef2f2' }, p: 0.5 }}>
                                                <RejectIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                )
                            ) : (
                                <L2StatusChip status={entry.l2_status} />
                            )}
                        </Box>

                        {/* Body */}
                        <Box sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{entry.project_name || '—'}</Typography>
                                <Box sx={{ display: 'inline-flex', px: 0.75, py: 0.2, bgcolor: '#f1f5f9', borderRadius: 1, border: '1px solid #e2e8f0', mt: 0.3 }}>
                                    <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: '#475569' }}>{entry.task_name || '—'}</Typography>
                                </Box>
                            </Box>
                            {entry.work_description && (
                                <Typography sx={{ fontSize: 11.5, color: '#475569', lineHeight: 1.5 }}>{entry.work_description}</Typography>
                            )}
                            {entry.remarks && (
                                <Typography sx={{ fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>{entry.remarks}</Typography>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
                                <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', px: 1, py: 0.4, bgcolor: '#f0f4ff', borderRadius: 1.5 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#1D4ED8', lineHeight: 1.2 }}>{entry.hours_worked}h</Typography>
                                    {Number(entry.overtime_hours) > 0 && (
                                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#f59e0b' }}>+{entry.overtime_hours}OT</Typography>
                                    )}
                                </Box>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.3, borderRadius: 10, bgcolor: entry.is_billable ? '#ecfdf5' : '#f5f3ff', border: `1px solid ${entry.is_billable ? '#6ee7b7' : '#ddd6fe'}` }}>
                                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: entry.is_billable ? '#059669' : '#7c3aed' }} />
                                    <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: entry.is_billable ? '#059669' : '#7c3aed' }}>
                                        {entry.is_billable ? 'Billable' : 'Non-Billable'}
                                    </Typography>
                                </Box>
                                {files.length > 0 && (
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {files.map((f, i) => (
                                            <Tooltip key={i} title={f.name}>
                                                <Box
                                                    component="a" href={f.url} target="_blank" rel="noopener noreferrer"
                                                    onClick={e => e.stopPropagation()}
                                                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, px: 0.6, py: 0.3, borderRadius: 1, bgcolor: '#eef2ff', border: '1px solid #c7d2fe', textDecoration: 'none', cursor: 'pointer', '&:hover': { bgcolor: '#e0e7ff' } }}
                                                >
                                                    <AttachIcon sx={{ fontSize: 11, color: '#6366f1' }} />
                                                    <OpenInNewIcon sx={{ fontSize: 9, color: '#818cf8' }} />
                                                </Box>
                                            </Tooltip>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* Inline edit panel */}
                        {isEditing && (
                            <Box sx={{ px: 1.5, py: 1.25, bgcolor: '#f5f3ff', borderTop: '2px solid #ddd6fe', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                    <TextField label="Hours" type="number" size="small"
                                        value={ev.hours_worked ?? ''}
                                        onChange={e => onEditChange(entry.id, 'hours_worked', e.target.value)}
                                        inputProps={{ min: 0.5, max: 24, step: 0.5 }}
                                        sx={{ width: 95 }} />
                                    <Select size="small"
                                        value={ev.is_billable ? 'billable' : 'non-billable'}
                                        onChange={e => onEditChange(entry.id, 'is_billable', e.target.value === 'billable')}
                                        sx={{ fontSize: 12, borderRadius: 1.5, height: 40, bgcolor: ev.is_billable ? '#ecfdf5' : '#f5f3ff', color: ev.is_billable ? '#059669' : '#7c3aed', '& .MuiOutlinedInput-notchedOutline': { borderColor: ev.is_billable ? '#6ee7b7' : '#ddd6fe' }, '& .MuiSvgIcon-root': { color: ev.is_billable ? '#059669' : '#7c3aed' }, '& .MuiSelect-select': { py: 0.75, px: 1.5 }, minWidth: 130 }}>
                                        <MenuItem value="billable" sx={{ fontSize: 12 }}>Billable</MenuItem>
                                        <MenuItem value="non-billable" sx={{ fontSize: 12 }}>Non-Billable</MenuItem>
                                    </Select>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Button size="small" variant="contained"
                                        onClick={() => onSaveEdit(entry.id)}
                                        disabled={editSaving}
                                        startIcon={editSaving ? <CircularProgress size={12} color="inherit" /> : null}
                                        sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#7c3aed', boxShadow: 'none', fontSize: 12 }}>
                                        {editSaving ? 'Saving…' : 'Save'}
                                    </Button>
                                    <Button size="small" onClick={onCancelEdit} disabled={editSaving}
                                        sx={{ textTransform: 'none', fontSize: 12, color: '#64748b' }}>Cancel</Button>
                                </Box>
                                {editError && (
                                    <Typography sx={{ fontSize: 11, color: '#dc2626' }}>{editError}</Typography>
                                )}
                            </Box>
                        )}
                    </Card>
                );
            })}
        </Box>
    );
}

function CompanyTimesheetEntries(props) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    if (isMobile) {
        return <CompanyEntryMobileCards {...props} />;
    }

    // Desktop: table view
    const { workingEntries, entryActing, editingId, editVals, onStartEdit, onCancelEdit, onEditChange, onSaveEdit, onApproveEntry, onRejectEntry, editSaving, editError } = props;
    return (
        <TableContainer>
            <Table sx={{ minWidth: 600, overflowX: 'auto' }}>
                <TableHead>
                    <TableRow sx={{ bgcolor: '#fafafa' }}>
                        {['Date', 'Project / Task', 'Description', 'Hours', 'Type', 'Files', 'Actions'].map(h => (
                            <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, color: '#64748b', py: 1, textTransform: 'uppercase', borderBottom: '1px solid #f1f5f9', ...(h === 'Actions' ? { textAlign: 'center' } : {}) }}>{h}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {workingEntries.map(entry => {
                        const dayName = entry.entry_date ? DAY_NAMES_SHORT[new Date(entry.entry_date + 'T00:00:00').getDay()] : '';
                        const isEditing = editingId === entry.id;
                        const ev = editVals[entry.id] || {};
                        const isApprovedUntouched = entry.l2_status === 'APPROVED' && !entry.l2_reopened_from_approved;
                        const isReopened = !!entry.l2_reopened_from_approved;
                        const statusBg = isApprovedUntouched ? '#f0fdf4' : isReopened ? '#eff6ff' : undefined;
                        const statusHoverBg = isApprovedUntouched ? '#dcfce7' : isReopened ? '#dbeafe' : '#fafbff';
                        return (
                            <React.Fragment key={entry.id}>
                                <TableRow sx={{ '&:hover': { bgcolor: statusHoverBg }, ...(statusBg ? { bgcolor: statusBg } : {}) }}>
                                    <TableCell sx={{ py: 1.75, borderBottom: '1px solid #f1f5f9' }}>
                                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{dayName}</Typography>
                                        <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{fmtDate(entry.entry_date)}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 1.75, borderBottom: '1px solid #f1f5f9' }}>
                                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a', mb: 0.5 }}>{entry.project_name || '—'}</Typography>
                                        <Box sx={{ display: 'inline-flex', px: 1, py: 0.35, bgcolor: '#f1f5f9', borderRadius: 1 }}>
                                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#475569' }}>{entry.task_name || '—'}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ py: 1.75, borderBottom: '1px solid #f1f5f9', maxWidth: 240 }}>
                                        <Typography sx={{ fontSize: 12, color: '#475569', lineHeight: 1.5 }}>{entry.work_description || '—'}</Typography>
                                        {entry.remarks && (
                                            <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.5, fontStyle: 'italic' }}>{entry.remarks}</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ py: 1.75, borderBottom: '1px solid #f1f5f9', width: 80 }}>
                                        <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{entry.hours_worked}h</Typography>
                                        {Number(entry.overtime_hours) > 0 && (
                                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#f59e0b' }}>+{entry.overtime_hours} OT</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ py: 1.75, borderBottom: '1px solid #f1f5f9' }}>
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.3, borderRadius: 10, bgcolor: entry.is_billable ? '#ecfdf5' : '#f5f3ff', border: `1px solid ${entry.is_billable ? '#6ee7b7' : '#ddd6fe'}` }}>
                                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: entry.is_billable ? '#059669' : '#7c3aed' }} />
                                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: entry.is_billable ? '#059669' : '#7c3aed' }}>
                                                {entry.is_billable ? 'Billable' : 'Non-Billable'}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ py: 1.75, borderBottom: '1px solid #f1f5f9' }}>
                                        <FilesButton attachments={entry.attachment_urls} />
                                    </TableCell>
                                    <TableCell sx={{ py: 1.75, borderBottom: '1px solid #f1f5f9', textAlign: 'center' }}>
                                        {(!entry.l2_status || entry.l2_status === 'PENDING') ? (
                                            entryActing?.[entry.id] ? (
                                                <CircularProgress size={16} sx={{ color: '#7c3aed' }} />
                                            ) : (
                                                <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'center', alignItems: 'center' }}>
                                                    {entry.external_can_edit && !isEditing && (
                                                        <Tooltip title="Edit entry">
                                                            <IconButton size="small" onClick={() => onStartEdit(entry)} sx={{ color: '#7c3aed', p: 0.5 }}>
                                                                <EditIcon sx={{ fontSize: 15 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip title={isEditing ? 'Save & Approve' : 'Approve entry'} arrow>
                                                        <IconButton size="small" onClick={() => onApproveEntry(entry)}
                                                            sx={{ color: '#059669', '&:hover': { bgcolor: '#ecfdf5' }, p: 0.5 }}>
                                                            <ApproveIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Reject entry" arrow>
                                                        <IconButton size="small" onClick={() => onRejectEntry(entry.id)}
                                                            sx={{ color: '#dc2626', '&:hover': { bgcolor: '#fef2f2' }, p: 0.5 }}>
                                                            <RejectIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            )
                                        ) : (
                                            <L2StatusChip status={entry.l2_status} />
                                        )}
                                    </TableCell>
                                </TableRow>

                                {/* INLINE EDIT ROW */}
                                {isEditing && (
                                    <TableRow key={`edit-${entry.id}`}>
                                        <TableCell colSpan={7} sx={{ bgcolor: '#f5f3ff', borderBottom: '2px solid #ddd6fe', px: 2.5, py: 1.5 }}>
                                            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                                <TextField label="Hours" type="number" size="small"
                                                    value={ev.hours_worked ?? ''}
                                                    onChange={e => onEditChange(entry.id, 'hours_worked', e.target.value)}
                                                    inputProps={{ min: 0.5, max: 24, step: 0.5 }}
                                                    sx={{ width: 95 }} />
                                                <Select
                                                    size="small"
                                                    value={ev.is_billable ? 'billable' : 'non-billable'}
                                                    onChange={e => onEditChange(entry.id, 'is_billable', e.target.value === 'billable')}
                                                    sx={{ fontSize: 12, borderRadius: 1.5, height: 40, bgcolor: ev.is_billable ? '#ecfdf5' : '#f5f3ff', color: ev.is_billable ? '#059669' : '#7c3aed', '& .MuiOutlinedInput-notchedOutline': { borderColor: ev.is_billable ? '#6ee7b7' : '#ddd6fe' }, '& .MuiSvgIcon-root': { color: ev.is_billable ? '#059669' : '#7c3aed' }, '& .MuiSelect-select': { py: 0.75, px: 1.5 }, minWidth: 130 }}>
                                                    <MenuItem value="billable" sx={{ fontSize: 12 }}>Billable</MenuItem>
                                                    <MenuItem value="non-billable" sx={{ fontSize: 12 }}>Non-Billable</MenuItem>
                                                </Select>
                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.25 }}>
                                                    <Button size="small" variant="contained"
                                                        onClick={() => onSaveEdit(entry.id)}
                                                        disabled={editSaving}
                                                        startIcon={editSaving ? <CircularProgress size={12} color="inherit" /> : null}
                                                        sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#7c3aed', boxShadow: 'none', fontSize: 12 }}>
                                                        {editSaving ? 'Saving…' : 'Save'}
                                                    </Button>
                                                    <Button size="small" onClick={onCancelEdit} disabled={editSaving}
                                                        sx={{ textTransform: 'none', fontSize: 12, color: '#64748b' }}>Cancel</Button>
                                                </Box>
                                            </Box>
                                            {editError && (
                                                <Typography sx={{ mt: 1, fontSize: 11, color: '#dc2626' }}>{editError}</Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </React.Fragment>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

// ─── Company Timesheet Card (COMPANY scope pre-approval) ──────────────────────

function CompanyTimesheetCard({ timesheet, acting, entryActing, onApprove, onReject, onApproveEntry, onRejectEntry, editingId, editVals, onStartEdit, onCancelEdit, onEditChange, onSaveEdit, editSaving, editError }) {
    const [expanded, setExpanded] = useState(true);
    const isActing = !!acting[timesheet.id];
    const workingEntries = (timesheet.entries || [])
        .filter(e => e.day_type === 'Working' && Number(e.hours_worked) > 0)
        .sort((a, b) => (a.entry_date || '').localeCompare(b.entry_date || ''));
    const totalHours = workingEntries.reduce((s, e) => s + Number(e.hours_worked || 0), 0);

    return (
        <Card elevation={0} sx={{ border: '1px solid #e9d5ff', borderRadius: 2.5, mb: 2, overflow: 'hidden' }}>
            <Box
                onClick={() => setExpanded(v => !v)}
                sx={{
                    px: 2.5, py: 1.5, bgcolor: '#f5f3ff',
                    borderBottom: expanded ? '1px solid #e9d5ff' : 'none',
                    display: 'flex', flexDirection: 'column', gap: 1, cursor: 'pointer',
                    '&:hover': { bgcolor: '#ede9fe' }, transition: 'background 0.15s',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: '#7c3aed', fontWeight: 800, fontSize: 13, borderRadius: 1.5, flexShrink: 0 }}>
                        {(timesheet.employeeAvatar || timesheet.employeeName?.split(' ').map(w => w[0]).join('') || '?').slice(0, 2).toUpperCase()}
                    </Avatar>
                    <Box sx={{ display: 'flex', alignItems: {xs: 'flex-start', sm: 'center'}, flexDirection: {xs: 'column', sm: 'row'}, flex: 1, gap: 1}}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#0f172a', lineHeight: 1.3 }}>{timesheet.employeeName}</Typography>
                            {timesheet.department_name && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.25 }}>
                                    <DeptIcon sx={{ fontSize: 11, color: '#94a3b8' }} />
                                    <Typography sx={{ fontSize: 11, color: '#64748b' }}>{timesheet.department_name}</Typography>
                                </Box>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                            <CalendarIcon sx={{ fontSize: 13, color: '#7c3aed' }} />
                            <Typography sx={{ fontSize: 12, color: '#64748b' }}>{fmtDate(timesheet.weekStart)} – {fmtDate(timesheet.weekEnd)}</Typography>
                        </Box>
                        <Chip label={`${workingEntries.length} entr${workingEntries.length === 1 ? 'y' : 'ies'} · ${totalHours}h`} size="small"
                            sx={{ bgcolor: '#ede9fe', color: '#6d28d9', border: '1px solid #ddd6fe', fontWeight: 700, fontSize: 10 }} />
                        {!!timesheet.resubmission_count && (
                            <Chip label="Resubmitted" size="small"
                                sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', border: '1px solid #93c5fd', fontWeight: 700, fontSize: 10 }} />
                        )}
                    </Box>
                    <IconButton size="small" sx={{ color: '#7c3aed', ml: 0.5, flexShrink: 0 }} onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}>
                        {expanded ? <CollapseIcon sx={{ fontSize: 20 }} /> : <ExpandIcon sx={{ fontSize: 20 }} />}
                    </IconButton>
                </Box>
                {timesheet.employeeComment && (
                    <Box sx={{ ml: { xs: 0, sm: `calc(36px + 12px)` }, mt: 0.5 }}>
                        <Typography sx={{ fontSize: 12, color: '#475569', fontStyle: 'italic'}}>
                            "{timesheet.employeeComment}"
                        </Typography>
                    </Box>
                )}
            </Box>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
                {workingEntries.length > 0 ? (
                    <CompanyTimesheetEntries
                        workingEntries={workingEntries}
                        entryActing={entryActing}
                        editingId={editingId}
                        editVals={editVals}
                        onStartEdit={onStartEdit}
                        onCancelEdit={onCancelEdit}
                        onEditChange={onEditChange}
                        onSaveEdit={onSaveEdit}
                        onApproveEntry={onApproveEntry}
                        onRejectEntry={onRejectEntry}
                        editSaving={editSaving}
                        editError={editError}
                    />
                ) : (
                    <Box sx={{ py: 3, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>No working entries found in this timesheet.</Typography>
                    </Box>
                )}

                <Box sx={{ px: 2.5, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5, bgcolor: '#f5f3ff', borderTop: '1px solid #e9d5ff', flexWrap: 'wrap' }}>
                    {isActing ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={20} sx={{ color: '#7c3aed' }} />
                            <Typography sx={{ fontSize: 12, color: '#7c3aed' }}>Processing...</Typography>
                        </Box>
                    ) : (
                        <>
                            <Button size="small" variant="outlined"
                                startIcon={<RejectIcon sx={{ fontSize: 14 }} />}
                                onClick={() => onReject(timesheet)}
                                sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, borderRadius: '8px', color: '#dc2626', borderColor: '#fca5a5', '&:hover': { bgcolor: '#fef2f2', borderColor: '#dc2626' } }}>
                                Reject All
                            </Button>
                            <Button size="small" variant="contained"
                                startIcon={<ApproveIcon sx={{ fontSize: 14 }} />}
                                onClick={() => onApprove(timesheet)}
                                sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, borderRadius: '8px', bgcolor: '#7c3aed', boxShadow: 'none', '&:hover': { bgcolor: '#6d28d9' } }}>
                                Approve All
                            </Button>
                        </>
                    )}
                </Box>
            </Collapse>
        </Card>
    );
}

// ─── Group Card entries (responsive: table on md+, cards on xs/sm) ────────────

function GroupCardEntries(props) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    return isMobile
        ? <EntryMobileCards {...props} />
        : <EntryTable {...props} />;
}

// ─── Group Card (collapsible) ─────────────────────────────────────────────────

function GroupCard({ group, expanded, onToggle, acting, editingId, editVals, onApprove, onReject, onStartEdit, onCancelEdit, onEditChange, onSaveEdit, editSaving, editError, showActions }) {
    const pendingCount = group.entries.filter(e => e.l2_status === 'PENDING').length;
    const totalHours = group.entries.reduce((s, e) => s + Number(e.hours_worked || 0), 0);

    return (
        <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2.5, mb: 2, overflow: 'hidden' }}>
            {/* Header — click anywhere to toggle */}
            <Box
                onClick={onToggle}
                sx={{
                    px: 2.5, py: 1.5, bgcolor: '#f8fafc',
                    borderBottom: expanded ? '1px solid #f1f5f9' : 'none',
                    display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1.5,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#f1f5f9' },
                    transition: 'background 0.15s',
                }}
            >
                <Avatar sx={{ width: 36, height: 36, bgcolor: '#6366f1', fontWeight: 800, fontSize: 13, borderRadius: 1.5, flexShrink: 0 }}>
                    {group.employee_name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                </Avatar>

                {/* Employee info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#0f172a', lineHeight: 1.3 }}>
                        {group.employee_name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.25, flexWrap: 'wrap' }}>
                        {group.employee_department && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                <DeptIcon sx={{ fontSize: 11, color: '#94a3b8' }} />
                                <Typography sx={{ fontSize: 11, color: '#64748b' }}>{group.employee_department}</Typography>
                            </Box>
                        )}
                        {group.employee_team && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                <TeamIcon sx={{ fontSize: 11, color: '#94a3b8' }} />
                                <Typography sx={{ fontSize: 11, color: '#64748b' }}>{group.employee_team}</Typography>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Week range */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                    <CalendarIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                    <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                        {fmtDate(group.week_start)} – {fmtDate(group.week_end)}
                    </Typography>
                </Box>

                {/* Entry count · total hours · pending badge */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    <Chip
                        label={`${group.entries.length} entr${group.entries.length === 1 ? 'y' : 'ies'} · ${totalHours}h`}
                        size="small"
                        sx={{ bgcolor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', fontWeight: 700, fontSize: 10 }}
                    />
                    {showActions && pendingCount > 0 && (
                        <Chip
                            label={`${pendingCount} pending`}
                            size="small"
                            sx={{ bgcolor: '#fff7ed', color: '#c2410c', border: '1px solid #fdba74', fontWeight: 700, fontSize: 10 }}
                        />
                    )}
                    {!!group.resubmission_count && pendingCount > 0 && (
                        <Chip
                            label="Resubmitted"
                            size="small"
                            sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', border: '1px solid #93c5fd', fontWeight: 700, fontSize: 10 }}
                        />
                    )}
                </Box>

                {/* Expand/collapse icon */}
                <IconButton size="small" sx={{ color: '#94a3b8', ml: 0.5, flexShrink: 0 }} onClick={e => { e.stopPropagation(); onToggle(); }}>
                    {expanded ? <CollapseIcon sx={{ fontSize: 20 }} /> : <ExpandIcon sx={{ fontSize: 20 }} />}
                </IconButton>
            </Box>

            {/* Collapsible entries */}
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <GroupCardEntries
                    group={group}
                    acting={acting}
                    editingId={editingId}
                    editVals={editVals}
                    onApprove={onApprove}
                    onReject={onReject}
                    onStartEdit={onStartEdit}
                    onCancelEdit={onCancelEdit}
                    onEditChange={onEditChange}
                    onSaveEdit={onSaveEdit}
                    editSaving={editSaving}
                    editError={editError}
                    showActions={showActions}
                />
            </Collapse>
        </Card>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function TimesheetL2Review() {
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userId = userData?.id;

    const [activeTab, setActiveTab] = useState(0);

    const [pendingEntries, setPendingEntries] = useState([]);
    const [allEntries, setAllEntries] = useState([]);
    const [loadingPending, setLoadingPending] = useState(true);
    const [loadingAll, setLoadingAll] = useState(false);
    const [fetchError, setFetchError] = useState('');

    // Collapse state: Set of group keys that are collapsed
    const [collapsedKeys, setCollapsedKeys] = useState(new Set());

    // Filters
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [year, setYear] = useState('All');
    const [month, setMonth] = useState(0);

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [acting, setActing] = useState({});
    const [editingId, setEditingId] = useState(null);
    const [editVals, setEditVals] = useState({});
    const [editSaving, setEditSaving] = useState(false);
    const [editError, setEditError] = useState('');

    const [approveDlg, setApproveDlg] = useState({ open: false, entry: null, comment: '' });
    const [rejectDlg, setRejectDlg] = useState({ open: false, entryId: null, comment: '', err: '' });
    const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

    // COMPANY scope pre-approval
    const [companyTimesheets, setCompanyTimesheets] = useState([]);
    const [loadingCompany, setLoadingCompany] = useState(false);
    const [actingCompany, setActingCompany] = useState({});
    const [companyApproveDlg, setCompanyApproveDlg] = useState({ open: false, timesheet: null, comment: '' });
    const [companyRejectDlg, setCompanyRejectDlg] = useState({ open: false, timesheet: null, comment: '', err: '' });

    // ── Fetch ──────────────────────────────────────────────────────────────────
    const loadPending = useCallback(async () => {
        setLoadingPending(true);
        setFetchError('');
        try {
            const res = await getData(API_ENDPOINTS.getL2PendingEntries);
            setPendingEntries(res?.result?.data || []);
        } catch {
            setFetchError('Failed to load pending entries. Please refresh.');
        } finally {
            setLoadingPending(false);
        }
    }, []);

    const loadAll = useCallback(async () => {
        if (!userId) return;
        setLoadingAll(true);
        setFetchError('');
        try {
            const res = await getData(API_ENDPOINTS.getL2ApproverItems(userId) + '?status=all');
            setAllEntries(res?.result?.data || []);
        } catch {
            setFetchError('Failed to load entries. Please refresh.');
        } finally {
            setLoadingAll(false);
        }
    }, [userId]);

    const loadCompanyTimesheets = useCallback(async () => {
        setLoadingCompany(true);
        try {
            const res = await getData(API_ENDPOINTS.getL2AssignedTimesheets);
            setCompanyTimesheets(res?.result?.data || []);
        } catch {
            // non-fatal — company section will simply not appear
        } finally {
            setLoadingCompany(false);
        }
    }, []);

    useEffect(() => { loadPending(); }, [loadPending]);
    useEffect(() => { loadCompanyTimesheets(); }, [loadCompanyTimesheets]);

    const handleRefresh = () => {
        setPage(0);
        loadPending();
        loadCompanyTimesheets();
        if (activeTab === 1) loadAll();
    };

    const handleTabChange = (_, v) => {
        setActiveTab(v);
        setPage(0);
        setSearch('');
        setStatusFilter('all');
        if (v === 1) loadAll();
    };

    // ── Group entries by employee + timesheet ──────────────────────────────────
    const groupEntries = (entries) => {
        const map = {};
        entries.forEach(e => {
            const key = `${e.employee_id}-${e.timesheet_id}`;
            if (!map[key]) {
                map[key] = {
                    key,
                    employee_name: e.employee_name || 'Unknown',
                    employee_department: e.employee_department || null,
                    employee_team: e.employee_team || null,
                    timesheet_id: e.timesheet_id,
                    week_start: e.timesheet_week_start,
                    week_end: e.timesheet_week_end,
                    resubmission_count: e.timesheet_resubmission_count || 0,
                    entries: [],
                };
            }
            map[key].entries.push(e);
        });
        // Weeks always start Monday, so sorting by calendar date lines entries
        // up Monday → Sunday regardless of the order the API returned them in.
        Object.values(map).forEach(g => {
            g.entries.sort((a, b) => (a.entry_date || '').localeCompare(b.entry_date || ''));
        });
        return Object.values(map);
    };

    const pendingGroups = useMemo(() => groupEntries(pendingEntries), [pendingEntries]);
    const allGroups = useMemo(() => groupEntries(allEntries), [allEntries]);

    const companyPendingTimesheets = useMemo(() =>
        companyTimesheets.filter(ts => ts.level2_approver_id && !ts.level2_approved_at && ts.status === 'Submitted'),
        [companyTimesheets]
    );

    // ── Filter & paginate ──────────────────────────────────────────────────────
    const rawGroups = activeTab === 0 ? pendingGroups : allGroups;

    const filteredGroups = useMemo(() => {
        let groups = rawGroups;

        if (search.trim()) {
            const q = search.toLowerCase();
            groups = groups.filter(g =>
                g.employee_name.toLowerCase().includes(q) ||
                (g.employee_department || '').toLowerCase().includes(q) ||
                (g.employee_team || '').toLowerCase().includes(q)
            );
        }

        if (activeTab === 1 && statusFilter !== 'all') {
            groups = groups.filter(g => g.entries.some(e => e.l2_status === statusFilter));
        }

        if (year !== 'All') {
            groups = groups.filter(g =>
                g.entries.some(e => e.entry_date && new Date(e.entry_date + 'T00:00:00').getFullYear() === year)
            );
        }

        if (month !== 0) {
            groups = groups.filter(g =>
                g.entries.some(e => e.entry_date && (new Date(e.entry_date + 'T00:00:00').getMonth() + 1) === month)
            );
        }

        return groups;
    }, [rawGroups, search, activeTab, statusFilter, year, month]);

    const pagedGroups = filteredGroups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // Reset to page 0 when filter changes
    useEffect(() => { setPage(0); }, [search, statusFilter, activeTab, year, month]);

    // ── Collapse toggle ────────────────────────────────────────────────────────
    const toggleCollapse = (key) => {
        setCollapsedKeys(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const collapseAll = () => setCollapsedKeys(new Set(pagedGroups.map(g => g.key)));
    const expandAll = () => setCollapsedKeys(new Set());

    // ── Approve ────────────────────────────────────────────────────────────────
    const openApproveDlg = (entry) => setApproveDlg({ open: true, entry, comment: '' });

    const confirmApprove = async () => {
        const { entry, comment } = approveDlg;
        const entryId = entry.id;
        setApproveDlg(p => ({ ...p, open: false }));
        setActing(p => ({ ...p, [entryId]: 'approving' }));
        try {
            const body = { comments: comment.trim() };
            await postData(API_ENDPOINTS.l2ApproveEntry(entryId), body);
            const belongsToCompanyTimesheet = companyTimesheets.some(ts => (ts.entries || []).some(e => e.id === entryId));
            const updater = (e) => e.id === entryId ? { ...e, l2_status: 'APPROVED', l2_comments: comment.trim() } : e;
            // Pending Approvals now shows the FULL timesheet (approved + pending siblings
            // together), so a plain local removal would drop this entry from view instead of
            // turning it into a status chip. Refetch so the list matches the server's actual
            // "does this timesheet still have anything pending" computation — the timesheet
            // only leaves this tab once every entry on it is resolved.
            await loadPending();
            setAllEntries(prev => prev.map(updater));
            if (belongsToCompanyTimesheet) {
                // Approving the LAST outstanding entry on a COMPANY-scope timesheet finalizes
                // level2_approved_at server-side — refetch so the card picks that up and drops
                // its "Approve All / Reject All" actions instead of staying stuck as pending.
                await loadCompanyTimesheets();
            } else {
                setCompanyTimesheets(prev => prev.map(ts => ({ ...ts, entries: (ts.entries || []).map(updater) })));
            }
            if (editingId === entryId) setEditingId(null);
            setSnack({ open: true, msg: 'Entry approved by external approver.', severity: 'success' });
        } catch (err) {
            setSnack({ open: true, msg: err?.response?.data?.message || 'Approve failed. Please try again.', severity: 'error' });
        } finally {
            setActing(p => ({ ...p, [entryId]: null }));
        }
    };

    // ── Reject ─────────────────────────────────────────────────────────────────
    const openRejectDlg = (entryId) => setRejectDlg({ open: true, entryId, comment: '', err: '' });

    const confirmReject = async () => {
        const { entryId, comment } = rejectDlg;
        if (!comment.trim()) { setRejectDlg(p => ({ ...p, err: 'A rejection reason is required.' })); return; }
        setRejectDlg(p => ({ ...p, open: false }));
        setActing(p => ({ ...p, [entryId]: 'rejecting' }));
        try {
            const res = await postData(API_ENDPOINTS.l2RejectEntry(entryId), { comments: comment.trim() });
            const allEntriesResolved = !!res?.result?.allEntriesResolved;
            const belongsToCompanyTimesheet = companyTimesheets.some(ts => (ts.entries || []).some(e => e.id === entryId));
            // See confirmApprove — refetch instead of locally removing the entry, so a
            // rejected-but-not-last entry still shows its status chip alongside its
            // still-pending siblings, and the timesheet only drops off once fully resolved.
            await loadPending();
            setAllEntries(prev => prev.map(e => e.id === entryId ? { ...e, l2_status: 'REJECTED', l2_comments: comment.trim() } : e));
            if (belongsToCompanyTimesheet) {
                // The timesheet only flips to REJECTED once every sibling entry has
                // been reviewed too — refetch so the card reflects the latest state
                // (still pending, or finally resolved) rather than just this entry.
                await loadCompanyTimesheets();
            }
            setSnack({
                open: true,
                msg: allEntriesResolved
                    ? 'Entry rejected — all entries reviewed, timesheet returned to employee.'
                    : 'Entry rejected — timesheet stays pending until the remaining entries are reviewed.',
                severity: 'warning',
            });
        } catch (err) {
            setSnack({ open: true, msg: err?.response?.data?.message || 'Reject failed. Please try again.', severity: 'error' });
        } finally {
            setActing(p => ({ ...p, [entryId]: null }));
        }
    };

    // ── Inline edit ────────────────────────────────────────────────────────────
    const startEdit = (entry) => {
        setEditingId(entry.id);
        setEditError('');
        setEditVals(p => ({
            ...p, [entry.id]: {
                hours_worked: entry.hours_worked ?? '',
                work_description: entry.work_description ?? '',
                remarks: entry.remarks ?? '',
                is_billable: entry.is_billable ?? false,
            }
        }));
    };
    const cancelEdit = () => { setEditingId(null); setEditError(''); };
    const handleEditChange = (id, field, val) => setEditVals(p => ({ ...p, [id]: { ...p[id], [field]: val } }));

    const saveEdit = async (entryId) => {
        const v = editVals[entryId] || {};
        if (v.hours_worked !== undefined && v.hours_worked !== '' && parseFloat(v.hours_worked) <= 0) {
            setEditError('Hours must be greater than 0.');
            return;
        }
        setEditSaving(true);
        setEditError('');
        try {
            const body = { is_billable: v.is_billable };
            if (v.hours_worked !== undefined && v.hours_worked !== '') body.hours_worked = parseFloat(v.hours_worked);
            await putData(API_ENDPOINTS.l2EditEntry(entryId), body);
            const updater = (e) => e.id === entryId ? {
                ...e,
                is_billable: v.is_billable,
                hours_worked: v.hours_worked !== '' ? parseFloat(v.hours_worked) : e.hours_worked,
            } : e;
            setPendingEntries(prev => prev.map(updater));
            setAllEntries(prev => prev.map(updater));
            setCompanyTimesheets(prev => prev.map(ts => ({ ...ts, entries: (ts.entries || []).map(updater) })));
            setEditingId(null);
            setSnack({ open: true, msg: 'Entry updated successfully.', severity: 'success' });
        } catch (err) {
            setEditError(err?.response?.data?.message || err?.message || 'Save failed. Please try again.');
        } finally {
            setEditSaving(false);
        }
    };

    // ── COMPANY scope handlers ─────────────────────────────────────────────────
    const openCompanyApproveDlg = (ts) => setCompanyApproveDlg({ open: true, timesheet: ts, comment: '' });
    const openCompanyRejectDlg = (ts) => setCompanyRejectDlg({ open: true, timesheet: ts, comment: '', err: '' });

    const confirmCompanyApprove = async () => {
        const { timesheet, comment } = companyApproveDlg;
        setCompanyApproveDlg(p => ({ ...p, open: false }));
        setActingCompany(p => ({ ...p, [timesheet.id]: 'approving' }));
        try {
            await postData(API_ENDPOINTS.companyL2ApproveTimesheet(timesheet.id), { comments: comment.trim() });
            setCompanyTimesheets(prev => prev.map(ts => ts.id === timesheet.id
                ? { ...ts, level2_approved_at: new Date().toISOString() }
                : ts
            ));
            // Approve-all resolves every PENDING entry on this timesheet server-side —
            // refetch so the "All Timesheets" tab's per-row status chips (driven off
            // allEntries) reflect APPROVED instead of staying stuck on PENDING.
            await loadAll();
            setSnack({ open: true, msg: 'Timesheet approved.', severity: 'success' });
        } catch (err) {
            setSnack({ open: true, msg: err?.response?.data?.message || 'Approval failed. Please try again.', severity: 'error' });
        } finally {
            setActingCompany(p => ({ ...p, [timesheet.id]: null }));
        }
    };

    const confirmCompanyReject = async () => {
        const { timesheet, comment } = companyRejectDlg;
        if (!comment.trim()) { setCompanyRejectDlg(p => ({ ...p, err: 'A rejection reason is required.' })); return; }
        setCompanyRejectDlg(p => ({ ...p, open: false }));
        setActingCompany(p => ({ ...p, [timesheet.id]: 'rejecting' }));
        try {
            await postData(API_ENDPOINTS.companyL2RejectTimesheet(timesheet.id), { comments: comment.trim() });
            setCompanyTimesheets(prev => prev.filter(ts => ts.id !== timesheet.id));
            // Reject-all resolves every PENDING entry on this timesheet server-side —
            // refetch so the "All Timesheets" tab's per-row status chips (driven off
            // allEntries) reflect REJECTED instead of staying stuck on PENDING.
            await loadAll();
            setSnack({ open: true, msg: 'Timesheet rejected — returned to employee for correction.', severity: 'warning' });
        } catch (err) {
            setSnack({ open: true, msg: err?.response?.data?.message || 'Reject failed. Please try again.', severity: 'error' });
        } finally {
            setActingCompany(p => ({ ...p, [timesheet.id]: null }));
        }
    };

    const loading = activeTab === 0 ? loadingPending : loadingAll;
    const pendingTotal = pendingGroups.length + companyPendingTimesheets.length;

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <Box sx={{ mx: 'auto' }}>

            {/* Page header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.25 }}>
                        Timesheet Management
                    </Typography>

                </Box>
                <Tooltip title="Refresh">
                    <IconButton onClick={handleRefresh} disabled={loading} sx={{ color: '#6366f1' }}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Tabs */}
            <Box sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0', mb: 2.5, px: 0.5, py: 0.5 }}>
                <Tabs value={activeTab} onChange={handleTabChange}
                    sx={{
                        minHeight: 44,
                        '& .MuiTabs-indicator': { display: 'none' },
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 13, minHeight: 40, borderRadius: 2, color: '#64748b', '&.Mui-selected': { color: '#6366f1', bgcolor: '#eef2ff' } },
                    }}>
                    <Tab
                        icon={<PendingIcon sx={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                Pending Approvals
                                {pendingTotal > 0 && (
                                    <Badge badgeContent={pendingTotal} color="warning"
                                        sx={{ '& .MuiBadge-badge': { fontSize: 10, fontWeight: 700, minWidth: 18, height: 18 } }}>
                                        <Box sx={{ width: 4 }} />
                                    </Badge>
                                )}
                            </Box>
                        }
                    />
                    <Tab icon={<AllIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="All Timesheets" />
                </Tabs>
            </Box>

            {/* Filter bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                {/* Search */}
                <TextField
                    size="small"
                    placeholder="Search by employee, dept or team"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                    }}
                    sx={{ width: 260, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
                />

                {/* Year */}
                <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel sx={{ fontSize: 13 }}>Year</InputLabel>
                    <Select
                        label="Year"
                        value={year}
                        onChange={e => setYear(e.target.value)}
                        sx={selectSx}
                    >
                        {YEARS.map(y => (
                            <MenuItem key={y} value={y} sx={{ fontSize: 13 }}>{y}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Month */}
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel sx={{ fontSize: 13 }}>Month</InputLabel>
                    <Select
                        label="Month"
                        value={month}
                        onChange={e => setMonth(e.target.value)}
                        sx={selectSx}
                    >
                        {MONTHS.map(m => (
                            <MenuItem key={m.value} value={m.value} sx={{ fontSize: 13 }}>{m.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Status filter — only on All tab */}
                {activeTab === 1 && (
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel sx={{ fontSize: 13 }}>Status</InputLabel>
                        <Select
                            label="Status"
                            value={statusFilter}
                            onChange={e => setStatusFilter(e.target.value)}
                            sx={selectSx}
                        >
                            {[
                                { value: 'all', label: 'All' },
                                { value: 'APPROVED', label: 'Approved' },
                                { value: 'REJECTED', label: 'Rejected' },
                            ].map(({ value, label }) => (
                                <MenuItem key={value} value={value} sx={{ fontSize: 13 }}>{label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {/* Collapse/Expand all */}
                {filteredGroups.length > 1 && (
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                        <Button size="small" variant="outlined" onClick={expandAll}
                            startIcon={<ExpandIcon sx={{ fontSize: 15 }} />}
                            sx={{ textTransform: 'none', fontSize: 12, borderRadius: 2, color: '#6366f1', borderColor: '#c7d2fe', '&:hover': { bgcolor: '#eef2ff' } }}>
                            Expand All
                        </Button>
                        <Button size="small" variant="outlined" onClick={collapseAll}
                            startIcon={<CollapseIcon sx={{ fontSize: 15 }} />}
                            sx={{ textTransform: 'none', fontSize: 12, borderRadius: 2, color: '#64748b', borderColor: '#e2e8f0', '&:hover': { bgcolor: '#f8fafc' } }}>
                            Collapse All
                        </Button>
                    </Box>
                )}
            </Box>

            {/* Result count */}
            {!loading && filteredGroups.length > 0 && (
                <Typography sx={{ fontSize: 12, color: '#94a3b8', mb: 1.5 }}>
                    {filteredGroups.length} result{filteredGroups.length !== 1 ? 's' : ''}
                </Typography>
            )}

            {/* COMPANY scope pre-approval section */}
            {activeTab === 0 && companyPendingTimesheets.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, pb: 1.25, borderBottom: '1px solid #e2e8f0' }}>
                        <CompanyIcon sx={{ fontSize: 16, color: '#7c3aed' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>
                            Timesheets level approval
                        </Typography>
                        <Chip label={companyPendingTimesheets.length} size="small"
                            sx={{ bgcolor: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe', fontWeight: 700, fontSize: 10, height: 20 }} />
                    </Box>
                    {companyPendingTimesheets.map(ts => (
                        <CompanyTimesheetCard
                            key={ts.id}
                            timesheet={ts}
                            acting={actingCompany}
                            entryActing={acting}
                            onApprove={openCompanyApproveDlg}
                            onReject={openCompanyRejectDlg}
                            onApproveEntry={openApproveDlg}
                            onRejectEntry={openRejectDlg}
                            editingId={editingId}
                            editVals={editVals}
                            onStartEdit={startEdit}
                            onCancelEdit={cancelEdit}
                            onEditChange={handleEditChange}
                            onSaveEdit={saveEdit}
                            editSaving={editSaving}
                            editError={editError}
                        />
                    ))}
                </Box>
            )}

            {/* Loading */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress sx={{ color: '#6366f1' }} />
                </Box>
            )}

            {/* Error */}
            {!loading && fetchError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{fetchError}</Alert>}

            {/* Empty state */}
            {!loading && !fetchError && filteredGroups.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 10 }}>
                    <EmptyIcon sx={{ fontSize: 60, color: '#e2e8f0', mb: 1.5 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#94a3b8' }}>
                        {search ? 'No results match your search' : activeTab === 0 ? 'No pending entries' : 'No entries found'}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: '#cbd5e1', mt: 0.5 }}>
                        {search ? 'Try a different name or department.' : activeTab === 0 ? 'All caught up — check back later.' : 'No entries have been assigned to you yet.'}
                    </Typography>
                </Box>
            )}

            {/* Group cards */}
            {!loading && pagedGroups.map(group => (
                <GroupCard
                    key={group.key}
                    group={group}
                    expanded={!collapsedKeys.has(group.key)}
                    onToggle={() => toggleCollapse(group.key)}
                    acting={acting}
                    editingId={editingId}
                    editVals={editVals}
                    onApprove={openApproveDlg}
                    onReject={openRejectDlg}
                    onStartEdit={startEdit}
                    onCancelEdit={cancelEdit}
                    onEditChange={handleEditChange}
                    onSaveEdit={saveEdit}
                    editSaving={editSaving}
                    editError={editError}
                    showActions={activeTab === 0}
                />
            ))}

            {/* Pagination */}
            {!loading && filteredGroups.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', borderTop: '1px solid #e2e8f0', mt: 2, px: 1 }}>
                    <Typography sx={{ fontSize: 11, color: '#94a3b8', flex: 1 }}>
                        {filteredGroups.length} group{filteredGroups.length !== 1 ? 's' : ''}
                    </Typography>
                    <TablePagination
                        component="div"
                        count={filteredGroups.length}
                        page={page}
                        onPageChange={(_, p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[5, 10, 25]}
                        labelRowsPerPage="Results per page:"
                        sx={{ border: 'none', '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: 12 } }}
                    />
                </Box>
            )}

            {/* ── Approve Dialog ─────────────────────────────────────────────── */}
            <Dialog open={approveDlg.open} onClose={() => setApproveDlg(p => ({ ...p, open: false }))}
                maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#059669' }}>Approve Entry</Typography>
                    <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.25 }}>Optionally add a comment before approving.</Typography>
                </DialogTitle>
                <DialogContent>
                    {approveDlg.entry && (
                        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>
                                {approveDlg.entry.project_name} — {approveDlg.entry.task_name}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.25 }}>
                                {fmtDay(approveDlg.entry.entry_date)}, {fmtDate(approveDlg.entry.entry_date)} · {approveDlg.entry.hours_worked}h
                            </Typography>
                        </Box>
                    )}
                    <TextField fullWidth multiline rows={3} label="Comment (optional)"
                        placeholder="Add any notes for the employee or manager..."
                        value={approveDlg.comment}
                        onChange={e => setApproveDlg(p => ({ ...p, comment: e.target.value }))}
                        sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => setApproveDlg(p => ({ ...p, open: false }))} sx={{ textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={confirmApprove}
                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, bgcolor: '#059669', boxShadow: 'none', '&:hover': { bgcolor: '#047857' } }}>
                        Confirm Approval
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Reject Dialog ──────────────────────────────────────────────── */}
            <Dialog open={rejectDlg.open} onClose={() => setRejectDlg(p => ({ ...p, open: false }))}
                maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#dc2626' }}>Reject Entry</Typography>
                    <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.25 }}>The timesheet will be returned to the employee for correction.</Typography>
                </DialogTitle>
                <DialogContent>
                    <TextField fullWidth multiline rows={3} label="Rejection reason"
                        placeholder="Describe what needs to be corrected..."
                        value={rejectDlg.comment}
                        onChange={e => setRejectDlg(p => ({ ...p, comment: e.target.value, err: '' }))}
                        error={!!rejectDlg.err} helperText={rejectDlg.err}
                        sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => setRejectDlg(p => ({ ...p, open: false }))} sx={{ textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={confirmReject} disabled={!rejectDlg.comment.trim()}
                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, bgcolor: '#dc2626', boxShadow: 'none', '&:hover': { bgcolor: '#b91c1c' } }}>
                        Confirm Rejection
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Company Pre-Approve Dialog ─────────────────────────── */}
            <Dialog open={companyApproveDlg.open} onClose={() => setCompanyApproveDlg(p => ({ ...p, open: false }))}
                maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#7c3aed' }}>Approve Timesheet</Typography>

                </DialogTitle>
                <DialogContent>
                    {companyApproveDlg.timesheet && (
                        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f5f3ff', borderRadius: 2, border: '1px solid #ddd6fe' }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{companyApproveDlg.timesheet.employeeName}</Typography>
                            <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.25 }}>
                                Week of {fmtDate(companyApproveDlg.timesheet.weekStart)} – {fmtDate(companyApproveDlg.timesheet.weekEnd)}
                            </Typography>
                        </Box>
                    )}
                    <TextField fullWidth multiline rows={3} label="Comment (optional)"
                        placeholder="Add any notes for the manager or employee..."
                        value={companyApproveDlg.comment}
                        onChange={e => setCompanyApproveDlg(p => ({ ...p, comment: e.target.value }))}
                        sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => setCompanyApproveDlg(p => ({ ...p, open: false }))} sx={{ textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={confirmCompanyApprove}
                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, bgcolor: '#7c3aed', boxShadow: 'none', '&:hover': { bgcolor: '#6d28d9' } }}>
                        Confirm Approve
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Company Reject Dialog ──────────────────────────────── */}
            <Dialog open={companyRejectDlg.open} onClose={() => setCompanyRejectDlg(p => ({ ...p, open: false }))}
                maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#dc2626' }}>Reject Timesheet</Typography>
                    <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.25 }}>The timesheet will be returned to the employee for correction.</Typography>
                </DialogTitle>
                <DialogContent>
                    <TextField fullWidth multiline rows={3} label="Rejection reason"
                        placeholder="Describe what needs to be corrected..."
                        value={companyRejectDlg.comment}
                        onChange={e => setCompanyRejectDlg(p => ({ ...p, comment: e.target.value, err: '' }))}
                        error={!!companyRejectDlg.err} helperText={companyRejectDlg.err}
                        sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => setCompanyRejectDlg(p => ({ ...p, open: false }))} sx={{ textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={confirmCompanyReject} disabled={!companyRejectDlg.comment.trim()}
                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, bgcolor: '#dc2626', boxShadow: 'none', '&:hover': { bgcolor: '#b91c1c' } }}>
                        Confirm Rejection
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(p => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snack.severity} onClose={() => setSnack(p => ({ ...p, open: false }))} sx={{ borderRadius: 2 }}>
                    {snack.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}
